import { QueryClient, QueryClientConfig } from '@tanstack/react-query';

const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      // Data is considered fresh for 1 minute
      staleTime: 1000 * 60,
      // Cache data for 5 minutes after last use
      gcTime: 1000 * 60 * 5,
      // Retry failed requests once
      retry: 1,
      // Don't refetch on window focus by default (can be overridden per query)
      refetchOnWindowFocus: false,
      // Refetch on reconnect
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
    },
  },
};

export const queryClient = new QueryClient(queryClientConfig);

// Query key factories for type-safe cache management
export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
  },

  // Projects
  projects: {
    all: ['projects'] as const,
    lists: () => [...queryKeys.projects.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.projects.lists(), filters] as const,
    details: () => [...queryKeys.projects.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.projects.details(), id] as const,
    members: (projectId: string) =>
      [...queryKeys.projects.detail(projectId), 'members'] as const,
    statuses: (projectId: string) =>
      [...queryKeys.projects.detail(projectId), 'statuses'] as const,
  },

  // Tasks
  tasks: {
    all: ['tasks'] as const,
    lists: () => [...queryKeys.tasks.all, 'list'] as const,
    byProject: (projectId: string) =>
      [...queryKeys.tasks.lists(), { projectId }] as const,
    byStatus: (projectId: string, statusId: string) =>
      [...queryKeys.tasks.lists(), { projectId, statusId }] as const,
    details: () => [...queryKeys.tasks.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.tasks.details(), id] as const,
  },

  // Comments
  comments: {
    all: ['comments'] as const,
    byTask: (taskId: string) => [...queryKeys.comments.all, { taskId }] as const,
    detail: (id: string) => [...queryKeys.comments.all, 'detail', id] as const,
  },

  // Attachments
  attachments: {
    all: ['attachments'] as const,
    byTask: (taskId: string) => [...queryKeys.attachments.all, { taskId }] as const,
    byComment: (commentId: string) =>
      [...queryKeys.attachments.all, { commentId }] as const,
  },

  // Notifications
  notifications: {
    all: ['notifications'] as const,
    list: (params?: { limit?: number; offset?: number }) =>
      [...queryKeys.notifications.all, 'list', params] as const,
    unreadCount: () => [...queryKeys.notifications.all, 'unread-count'] as const,
  },

  // Search
  search: {
    all: ['search'] as const,
    tasks: (query: string, filters?: Record<string, unknown>) =>
      [...queryKeys.search.all, 'tasks', query, filters] as const,
  },
} as const;
